import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap } from 'rxjs/operators';

import {
  ListenTypInfo,
  SPS_LISTEN_SORTIERUNGEN,
  SPS_LISTEN_TEXTFELDER,
  SPS_PRUEFSTATUS_OPTIONEN,
  SpsAnlagenteilOptionen,
  SpsEintragLoeschvorschau,
  SpsImportEintrag,
  SpsImportListe,
  SpsImportListeDetail,
  SpsListenFilter,
  SpsListenSortierung,
  SpsListenUpdateErgebnis,
  SpsPruefstatus,
  SpsPruefstatusOption,
  SpsSeite,
  SpsSignalRichtung,
  anlagenteilAuswahlLesen,
  anlagenteilAuswahlWert,
  leereSpsAnlagenteilOptionen,
  leererSpsListenFilter,
  listenTypInfo,
  pruefstatusOption,
  pruefstatusText,
  richtungText,
  signalFolgeText,
  spsListenFilterAktiv,
  spsListenFilterAlsParams,
  spsListenFilterAusParams
} from '../../../../../Models/Sps';
import { AuthService } from '../../../../../api-authorization/authorize.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import { SpsImportApiService } from '../../../../../Services/sps-import-api.service';
import { SpsListenLoeschenService } from '../../../../../Services/sps-listen-loeschen.service';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';
import { SpsListenUpdateDialogComponent, SpsListenUpdateDialogData } from '../sps-listen-update-dialog/sps-listen-update-dialog.component';

/** Eine Tabellenspalte der Listenansicht: Überschrift und Wert je Eintrag. */
export interface SpsListenSpalte {
  schluessel: string;
  titel: string;
  wert: (e: SpsImportEintrag) => string;
  /** CSS-Klasse für die Breite. */
  klasse?: string;
}

/** Spaltenansicht: fachliche Spalten des Listentyps oder alle Excel-Spalten. */
export type SpsListenAnsicht = 'standard' | 'original';

/**
 * Anzeige EINER importierten SPS-Liste (SPS-Zuordnungsliste oder PLC-Tag-Liste)
 * mit ihren originalen Excel-Zeilen – nicht die zusammengeführten Signale der
 * SPS-Prüfung. Beide Typen teilen sich dieselbe Komponente, weil das Backend
 * sie gleich liefert (`SpsImportListeDetail` + `SpsImportEintrag` mit
 * `originalDaten`); nur die Spalten unterscheiden sich.
 *
 * Suche, Richtung, Prüfstatus, Feldfilter, Sortierung und Blättern laufen
 * serverseitig über alle Zeilen der Liste (GET api/sps/listen/{id}/eintraege).
 * Der Filterzustand (samt Seite und Spaltenansicht) steht in der URL und
 * überlebt so den Wechsel in die Signaldetails und zurück.
 */
@Component({
  selector: 'app-sps-liste',
  templateUrl: './sps-liste.component.html',
  styleUrls: ['./sps-liste.component.css']
})
export class SpsListeComponent implements OnInit, OnDestroy {

  projektId = 0;
  listenId = 0;

  liste: SpsImportListeDetail | null = null;
  typInfo: ListenTypInfo = listenTypInfo('SpsZuordnungsliste');

  seite: SpsSeite<SpsImportEintrag> | null = null;

  /** Aktueller Filter – entspricht den Query-Parametern von GET api/sps/listen/{id}/eintraege. */
  filter: SpsListenFilter = leererSpsListenFilter();

  /**
   * Optionen des Anlagenteil-Filters: nur die Anlagenteile, die in DIESER Liste
   * tatsächlich vorkommen (über alle Zeilen, nicht nur die Seite), alphabetisch.
   * Nicht die Zuordnungsauswahl – die zeigt weiterhin alle Anlagenteile des Projekts.
   */
  anlagenteilOptionen: SpsAnlagenteilOptionen = leereSpsAnlagenteilOptionen();

  /** Liste, zu der `anlagenteilOptionen` gehört, und Nummer der letzten Anfrage – ältere Antworten werden verworfen. */
  private anlagenteileListenId = 0;
  private anlagenteilAnfrage = 0;

  /** Suchbegriff im Feld; `aktiveSuche` ist der zuletzt angewendete. */
  suche = '';
  aktiveSuche = '';
  aktuelleSeite = 1;
  seitenGroesse = 100;
  readonly seitenGroessen = [50, 100, 250, 500];

  ansicht: SpsListenAnsicht = 'standard';

  readonly statusOptionen: SpsPruefstatusOption[] = SPS_PRUEFSTATUS_OPTIONEN;
  readonly sortierungen = SPS_LISTEN_SORTIERUNGEN;

  erweitert = false;

  laedtListe = false;
  laedtEintraege = false;
  loescht = false;
  fehler = '';

  /** Ids der Einträge, deren Löschen gerade läuft (nur diese Zeilen sind gesperrt). */
  private eintragLoeschend = new Set<number>();

  /** Nummer der zuletzt gestarteten Anfrage – ältere Antworten werden verworfen. */
  private anfrage = 0;

  private readonly filterEingabe = new Subject<void>();
  private subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: SpsImportApiService,
    private loeschen: SpsListenLoeschenService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.subs.add(
      this.filterEingabe.pipe(debounceTime(350)).subscribe(() => this.filterAnwenden()));

    // Nur die Pfadparameter werden beobachtet. Die Query-Parameter schreibt die
    // Komponente selbst (replaceUrl) und liest sie beim Öffnen einmal – kein Kreislauf.
    this.subs.add(
      this.route.paramMap.subscribe(params => {
        this.projektId = Number(params.get('projektId')) || 0;
        this.listenId = Number(params.get('listenId')) || 0;
        this.filterAusUrlUebernehmen();
        this.ladeListe();
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.filterEingabe.complete();
  }

  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  private filterAusUrlUebernehmen(): void {
    const q = this.route.snapshot.queryParamMap;

    this.filter = spsListenFilterAusParams(q);
    this.suche = this.filter.suche;
    this.aktiveSuche = this.filter.suche;
    this.aktuelleSeite = this.filter.seite;
    this.seitenGroesse = this.filter.groesse;
    this.ansicht = q.get('ansicht') === 'original' ? 'original' : 'standard';
    this.erweitert = SPS_LISTEN_TEXTFELDER.some(k => k !== 'suche' && this.filter[k].trim() !== '');
  }


  // =========================================================
  // LADEN
  // =========================================================

  ladeListe(): void {
    if (!this.listenId) {
      this.fehler = 'Ungültige Listen-Id.';
      return;
    }

    this.laedtListe = true;
    this.fehler = '';

    this.api.getListe(this.listenId)
      .pipe(finalize(() => this.laedtListe = false))
      .subscribe({
        next: liste => {
          this.liste = liste;
          this.typInfo = listenTypInfo(liste.listenTyp);
          this.ladeAnlagenteile();
          this.ladeEintraege();
        },
        error: err => {
          this.liste = null;
          this.seite = null;
          this.fehler = err?.status === 404
            ? 'Die Liste existiert nicht mehr oder ist nicht zugänglich.'
            : SpsPruefungApiService.fehlertext(err, 'Die Liste konnte nicht geladen werden.');
        }
      });
  }

  ladeEintraege(): void {
    if (!this.listenId) {
      return;
    }

    const nummer = ++this.anfrage;
    this.laedtEintraege = true;
    this.fehler = '';

    this.filter.seite = this.aktuelleSeite;
    this.filter.groesse = this.seitenGroesse;
    this.filter.suche = this.aktiveSuche;

    this.api.getEintraege(this.listenId, this.filter)
      .pipe(finalize(() => {
        if (nummer === this.anfrage) {
          this.laedtEintraege = false;
        }
      }))
      .subscribe({
        next: seite => {
          if (nummer !== this.anfrage) {
            return;   // eine neuere Anfrage läuft bereits
          }

          this.seite = seite;

          // Seite hinter dem Ende (z. B. nach dem Filtern): auf die letzte Seite springen.
          const letzte = Math.max(1, Math.ceil(seite.gesamt / seite.groesse));
          if (seite.seite > letzte && seite.gesamt > 0) {
            this.aktuelleSeite = letzte;
            this.urlAktualisieren();
            this.ladeEintraege();
          }
        },
        error: err => {
          if (nummer === this.anfrage) {
            this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Einträge konnten nicht geladen werden.');
          }
        }
      });
  }

  /**
   * Filteroptionen „Anlagenteil“ – listenbezogen: nur Anlagenteile, die in dieser
   * Liste wirklich vorkommen, serverseitig über alle Zeilen ermittelt. Wird nach
   * jedem Laden der Kopfdaten (Import, Update, Aktualisieren) und nach dem
   * Löschen einer Zeile neu geholt. Ein Fehler lässt nur das Auswahlfeld leer;
   * Liste und Filter funktionieren weiter.
   */
  ladeAnlagenteile(): void {
    const listenId = this.listenId;
    const nummer = ++this.anlagenteilAnfrage;

    // Listenwechsel: nie die Optionen der vorherigen Liste stehen lassen.
    if (listenId !== this.anlagenteileListenId) {
      this.anlagenteilOptionen = leereSpsAnlagenteilOptionen();
      this.anlagenteileListenId = listenId;
    }

    if (!listenId) {
      return;
    }

    this.api.getAnlagenteile(listenId).subscribe({
      next: optionen => {
        // Eine verspätete Antwort einer früheren Liste überschreibt nichts.
        if (nummer === this.anlagenteilAnfrage && listenId === this.listenId) {
          this.anlagenteilOptionen = optionen;
        }
      },
      error: () => {
        if (nummer === this.anlagenteilAnfrage) {
          this.anlagenteilOptionen = leereSpsAnlagenteilOptionen();
        }
      }
    });
  }

  /** Kopfdaten und aktuelle Seite neu laden – Filter, Sortierung und Seite bleiben. */
  aktualisieren(): void {
    this.ladeListe();
  }


  // =========================================================
  // FILTER, SUCHE UND BLÄTTERN
  // =========================================================

  /** Texteingaben (Suche und Feldfilter) – mit kurzer Verzögerung anwenden. */
  textGeaendert(): void {
    this.filterEingabe.next();
  }

  /** Enter im Suchfeld: sofort anwenden. */
  sucheAnwenden(): void {
    this.filter.suche = this.suche;
    this.filterAnwenden();
  }

  sucheLeeren(): void {
    this.suche = '';
    this.filter.suche = '';
    this.filterAnwenden();
  }

  richtungSetzen(richtung: SpsSignalRichtung | null): void {
    if (this.filter.richtung === richtung) {
      return;
    }

    this.filter.richtung = richtung;
    this.filterAnwenden();
  }

  statusSetzen(status: SpsPruefstatus | null): void {
    this.filter.status = status;
    this.filterAnwenden();
  }

  nurOffeneSetzen(wert: boolean): void {
    this.filter.nurOffen = wert;
    this.filterAnwenden();
  }

  /** Wert der Anlagenteil-Auswahl: null = alle, 'ohne' = ohne Anlagenteil, sonst die Id. */
  get anlagenteilAuswahl(): number | string | null {
    return anlagenteilAuswahlWert(this.filter.anlagenteilId, this.filter.ohneAnlagenteil);
  }

  anlagenteilSetzen(wert: number | string | null): void {
    const auswahl = anlagenteilAuswahlLesen(wert);
    this.filter.anlagenteilId = auswahl.anlagenteilId;
    this.filter.ohneAnlagenteil = auswahl.ohneAnlagenteil;
    this.filterAnwenden();
  }

  /**
   * Der gefilterte Anlagenteil kommt in dieser Liste (nicht mehr) vor – z. B. aus
   * der URL eines anderen Stands oder weil seine letzte Zeile gelöscht wurde.
   * Dann bleibt er nur als aktive Auswahl sichtbar, statt dass das Feld leer
   * aussieht, obwohl gefiltert wird; mit der nächsten Auswahl verschwindet er.
   */
  get anlagenteilAuswahlFehlt(): boolean {
    return this.filter.anlagenteilId != null
      && !this.anlagenteilOptionen.anlagenteile.some(a => a.anlagenteilId === this.filter.anlagenteilId);
  }

  /** „Ohne Anlagenteil“ nur anbieten, wenn es solche Zeilen gibt – oder der Filter gerade aktiv ist. */
  get zeigeOhneAnlagenteil(): boolean {
    return this.anlagenteilOptionen.ohneAnlagenteil > 0 || this.filter.ohneAnlagenteil;
  }

  /** Das Filterfeld entfällt, wenn die Liste keinen einzigen Anlagenteil kennt und nichts gefiltert wird. */
  get zeigeAnlagenteilFilter(): boolean {
    return this.anlagenteilOptionen.anlagenteile.length > 0
      || this.filter.anlagenteilId != null
      || this.filter.ohneAnlagenteil;
  }

  sortierungSetzen(wert: SpsListenSortierung): void {
    this.filter.sortierung = wert;
    this.filterAnwenden();
  }

  erweitertUmschalten(): void {
    this.erweitert = !this.erweitert;
  }

  /** Alle Bedingungen und die Sortierung zurücksetzen; die Seitengröße bleibt. */
  filterZuruecksetzen(): void {
    const groesse = this.seitenGroesse;
    this.filter = { ...leererSpsListenFilter(), groesse };
    this.suche = '';
    this.aktiveSuche = '';
    this.erweitert = false;
    this.filterAnwenden();
  }

  get filterAktiv(): boolean {
    return spsListenFilterAktiv({ ...this.filter, suche: this.suche });
  }

  private filterAnwenden(): void {
    this.aktuelleSeite = 1;
    this.aktiveSuche = (this.filter.suche ?? this.suche).trim();
    this.suche = this.filter.suche = this.aktiveSuche;
    this.urlAktualisieren();
    this.ladeEintraege();
  }

  seiteGewechselt(event: PageEvent): void {
    this.aktuelleSeite = event.pageIndex + 1;

    if (event.pageSize !== this.seitenGroesse) {
      this.seitenGroesse = event.pageSize;
      this.aktuelleSeite = 1;
    }

    this.urlAktualisieren();
    this.ladeEintraege();
  }

  ansichtGeaendert(ansicht: SpsListenAnsicht): void {
    this.ansicht = ansicht;
    this.urlAktualisieren();
  }

  get seitenAnzahl(): number {
    return this.seite ? Math.max(1, Math.ceil(this.seite.gesamt / this.seite.groesse)) : 1;
  }

  /** Query-Parameter des aktuellen Zustands (Filter, Seite, Größe, Spaltenansicht). */
  private zustandAlsParams(): Params {
    const p = spsListenFilterAlsParams({
      ...this.filter,
      seite: this.aktuelleSeite,
      groesse: this.seitenGroesse,
      suche: this.aktiveSuche
    });

    if (this.ansicht === 'original') {
      p['ansicht'] = 'original';
    }

    return p;
  }

  private urlAktualisieren(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.zustandAlsParams(),
      replaceUrl: true
    });
  }


  // =========================================================
  // SPALTEN
  // =========================================================

  /** Fachliche Spalten des Listentyps – die tatsächlich gespeicherten Felder sind maßgeblich. */
  get standardSpalten(): SpsListenSpalte[] {
    const wert = (v: string | null | undefined) => v ?? '';

    // Anlagenteil am Zeilenanfang (als Chip, siehe Template): wenn die Liste selbst
    // Anlagenteile liefert (Ort bzw. Path) ODER eine Zeile der Seite einen über das
    // gemeinsame Signal aus der anderen Liste übernommen hat. „Ort“ bleibt daneben
    // als eigene Spalte erhalten.
    const anlagenteil: SpsListenSpalte[] = this.zeigeAnlagenteilSpalte
      ? [{ schluessel: 'anlagenteil', titel: 'Anlagenteil', wert: e => wert(e.anlagenteilName), klasse: 'sp-anlagenteil' }]
      : [];

    if (this.liste?.listenTyp === 'PlcTagListe') {
      return [
        ...anlagenteil,
        { schluessel: 'signalname', titel: 'Signalname / Tag', wert: e => wert(e.signalname), klasse: 'sp-name' },
        { schluessel: 'adresse', titel: 'SPS-Adresse', wert: e => wert(e.adresseOriginal), klasse: 'sp-adresse' },
        { schluessel: 'beschreibung', titel: 'Beschreibung', wert: e => wert(e.beschreibung), klasse: 'sp-beschreibung' },
        { schluessel: 'richtung', titel: 'Richtung', wert: e => richtungText(e.richtung), klasse: 'sp-kurz' },
        { schluessel: 'datentyp', titel: 'Datentyp', wert: e => wert(e.datenTyp), klasse: 'sp-kurz' },
        { schluessel: 'station', titel: 'Station / Pfad', wert: e => wert(e.stationsName), klasse: 'sp-station' },
        { schluessel: 'bmk', titel: 'BMK', wert: e => wert(e.bmk), klasse: 'sp-kurz' }
      ];
    }

    return [
      ...anlagenteil,
      { schluessel: 'signalname', titel: 'Signal', wert: e => wert(e.signalname), klasse: 'sp-name' },
      { schluessel: 'bmk', titel: 'BMK', wert: e => wert(e.bmk), klasse: 'sp-kurz' },
      { schluessel: 'beschreibung', titel: 'Beschreibung', wert: e => wert(e.beschreibung), klasse: 'sp-beschreibung' },
      { schluessel: 'richtung', titel: 'Richtung', wert: e => richtungText(e.richtung), klasse: 'sp-kurz' },
      { schluessel: 'adresse', titel: 'SPS-Adresse', wert: e => wert(e.adresseOriginal), klasse: 'sp-adresse' },
      { schluessel: 'station', titel: 'Station / SPS', wert: e => wert(e.stationsName), klasse: 'sp-station' },
      { schluessel: 'anschluss', titel: 'Anschluss', wert: e => wert(e.anschluss), klasse: 'sp-kurz' },
      { schluessel: 'kanal', titel: 'Kanal', wert: e => wert(e.kanal), klasse: 'sp-kurz' },
      { schluessel: 'ort', titel: 'Ort', wert: e => wert(e.ort), klasse: 'sp-kurz' },
      { schluessel: 'platzierung', titel: 'Platzierung', wert: e => wert(e.platzierung), klasse: 'sp-kurz' }
    ];
  }

  /** Anlagenteil-Spalte zeigen, sobald diese Liste oder die aktuelle Seite Anlagenteile kennt. */
  get zeigeAnlagenteilSpalte(): boolean {
    return !!this.liste?.anlagenteilSpalte
      || this.filter.anlagenteilId != null
      || (this.seite?.eintraege ?? []).some(e => !!e.anlagenteilName);
  }

  /** Alle Excel-Spalten der Datei in Originalreihenfolge. */
  get originalSpalten(): SpsListenSpalte[] {
    return (this.liste?.spalten ?? []).map(name => ({
      schluessel: 'orig:' + name,
      titel: name,
      wert: (e: SpsImportEintrag) => e.originalDaten?.[name] ?? ''
    }));
  }

  get spalten(): SpsListenSpalte[] {
    return this.ansicht === 'original' && this.originalSpalten.length > 0
      ? this.originalSpalten
      : this.standardSpalten;
  }

  statusOption(status: SpsPruefstatus | null): SpsPruefstatusOption {
    return pruefstatusOption(status);
  }

  trackByEintrag(_index: number, e: SpsImportEintrag): number {
    return e.id;
  }

  trackBySpalte(_index: number, s: SpsListenSpalte): string {
    return s.schluessel;
  }


  // =========================================================
  // AKTIONEN UND NAVIGATION
  // =========================================================

  /**
   * Ein verknüpftes SPS-Signal öffnen – führt zur gemeinsamen Prüfung des
   * Signals. Der Listenzustand geht als Query-Parameter mit, damit „Zurück“
   * dieselbe Filterung, Seite und Spaltenansicht wiederfindet.
   */
  oeffneSignal(eintrag: SpsImportEintrag, event?: Event): void {
    event?.stopPropagation();

    if (eintrag.signalId == null) {
      return;
    }

    void this.router.navigate(['/projekte', this.projektId, 'sps-signale', eintrag.signalId], {
      queryParams: { von: 'liste', listenId: this.listenId, ...this.zustandAlsParams() }
    });
  }

  listeLoeschen(): void {
    if (!this.liste || !this.canEdit || this.loescht) {
      return;
    }

    const liste: SpsImportListe = this.liste;
    this.loescht = true;

    this.loeschen.loeschen(liste)
      .pipe(finalize(() => this.loescht = false))
      .subscribe(ergebnis => {
        if (ergebnis) {
          this.zurUebersicht();
        }
      });
  }

  /**
   * „Liste aktualisieren“: neuere Excel-Version derselben Liste hochladen –
   * Vorschau und Bestätigung im Dialog, danach Kopfdaten und Seite neu laden.
   */
  listeAktualisieren(): void {
    if (!this.liste || !this.canEdit || this.loescht) {
      return;
    }

    const data: SpsListenUpdateDialogData = { liste: this.liste };

    this.dialog.open<SpsListenUpdateDialogComponent, SpsListenUpdateDialogData, SpsListenUpdateErgebnis | null>(
      SpsListenUpdateDialogComponent, { data, width: '760px', maxWidth: '95vw', autoFocus: false, disableClose: true }
    ).afterClosed().subscribe(ergebnis => {
      if (!ergebnis) {
        return;
      }

      this.snackBar.open(SpsListeComponent.updateErgebnisText(ergebnis), 'OK', { duration: 7000 });
      this.ladeListe();
    });
  }

  static updateErgebnisText(e: SpsListenUpdateErgebnis): string {
    const teile = [`„${e.liste.name}“ aktualisiert: ${e.aktualisiert} geändert, ${e.neu} neu, ${e.geloescht} gelöscht, ${e.unveraendert} unverändert.`];

    if (e.geprueftErhalten > 0) {
      teile.push(`${e.geprueftErhalten} Prüfstatus erhalten.`);
    }

    if (e.seitPruefungGeaendert > 0) {
      teile.push(`${e.seitPruefungGeaendert} als „Seit Prüfung geändert“ gekennzeichnet.`);
    }

    if (e.konflikte > 0) {
      teile.push(`${e.konflikte} Konflikte unverändert.`);
    }

    if (e.uebersprungen > 0) {
      teile.push(`${e.uebersprungen} zuvor gelöschte Zeilen nicht wiederhergestellt.`);
    }

    return teile.join(' ');
  }


  // =========================================================
  // EINZELNEN EINTRAG LÖSCHEN
  // =========================================================

  wirdGeloescht(e: SpsImportEintrag): boolean {
    return this.eintragLoeschend.has(e.id);
  }

  /**
   * Löscht eine Originalzeile dieser Liste. Vorher zeigt ein Dialog, was mit
   * dem verknüpften Signal passiert (bleibt / archiviert / entfernt).
   */
  eintragLoeschen(e: SpsImportEintrag, event?: Event): void {
    event?.stopPropagation();

    if (!this.liste || !this.canEdit || this.wirdGeloescht(e)) {
      return;
    }

    const listenId = this.listenId;
    this.eintragLoeschend.add(e.id);

    this.api.getEintragLoeschvorschau(listenId, e.id).pipe(
      catchError(() => of(null)),
      switchMap(vorschau => this.dialog
        .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
          data: SpsListeComponent.eintragLoeschDialog(e, vorschau),
          width: '480px',
          maxWidth: '95vw',
          autoFocus: false
        })
        .afterClosed()),
      switchMap(bestaetigt => bestaetigt
        ? this.api.deleteEintrag(listenId, e.id).pipe(
            catchError(err => {
              this.snackBar.open(SpsPruefungApiService.fehlertext(err, 'Der Eintrag konnte nicht gelöscht werden.'), 'OK', { duration: 6000 });
              return of(null);
            }))
        : of(null)),
      finalize(() => this.eintragLoeschend.delete(e.id))
    ).subscribe(ergebnis => {
      if (!ergebnis) {
        return;
      }

      const text = ergebnis.signalFolge === 'SignalBleibt'
        ? 'Eintrag gelöscht. Das Signal bleibt über seine andere Quelle in der Prüfung.'
        : ergebnis.signalFolge === 'SignalArchiviert'
          ? 'Eintrag gelöscht. Das geprüfte Signal wurde archiviert.'
          : ergebnis.signalFolge === 'SignalGeloescht'
            ? 'Eintrag und sein ungeprüftes Signal gelöscht.'
            : 'Eintrag gelöscht.';

      this.snackBar.open(text, 'OK', { duration: 5000 });

      if (this.liste) {
        this.liste = { ...this.liste, eintraegeAnzahl: Math.max(0, this.liste.eintraegeAnzahl - 1) };
      }

      // War es die letzte Zeile mit diesem Anlagenteil, verschwindet er aus dem Filter.
      this.ladeAnlagenteile();
      this.ladeEintraege();
    });
  }

  static eintragLoeschDialog(e: SpsImportEintrag, vorschau: SpsEintragLoeschvorschau | null): ConfirmDialogData {
    const details: string[] = [
      `Zeile ${e.excelZeile}: ${e.signalname || '–'}${e.adresseOriginal ? ' · ' + e.adresseOriginal : ''}`
    ];

    if (vorschau) {
      if (vorschau.signalId == null) {
        details.push(vorschau.ausPruefungEntfernt
          ? 'Die Zeile war bereits aus der SPS-Prüfung entfernt.'
          : 'Die Zeile ist mit keinem Signal verknüpft.');
      } else {
        details.push(`Verknüpftes Signal: ${vorschau.signalName || '–'}${vorschau.geprueft ? ' (geprüft: ' + pruefstatusText(vorschau.pruefstatus) + ')' : ' (ungeprüft)'}.`);
        details.push(signalFolgeText(vorschau.folge));

        for (const q of vorschau.weitereQuellen) {
          details.push(`Weitere Quelle bleibt: ${listenTypInfo(q.listeTyp === 'PlcTags' ? 'PlcTagListe' : 'SpsZuordnungsliste').titel} „${q.listeName}“, Zeile ${q.excelZeile}.`);
        }
      }
    } else {
      details.push('Die Auswirkungen auf das Signal konnten nicht vorab ermittelt werden.');
    }

    return {
      title: 'Eintrag löschen?',
      message: 'Die Originalzeile wird aus dieser Liste entfernt. Andere Zeilen und andere Listen bleiben unverändert.',
      details,
      warning: 'Dieser Schritt kann nicht rückgängig gemacht werden. Ein späteres Listenupdate stellt die Zeile nur auf ausdrücklichen Wunsch wieder her.',
      confirmText: 'Eintrag löschen'
    };
  }

  zurUebersicht(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-listen']);
  }

  zurSpsPruefung(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-pruefung']);
  }

  zurueckZumProjekt(): void {
    void this.router.navigate(['/projekte', this.projektId]);
  }
}
